import { useToastStore, Toast, ToastType } from '../../stores/toastStore';

const ICONS: Record<ToastType, React.ReactNode> = {
  success: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  info: (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const STYLES: Record<ToastType, { wrapper: string; icon: string; bar: string }> = {
  success: { wrapper: 'border-l-4 border-emerald-500', icon: 'text-emerald-500', bar: 'bg-emerald-500' },
  error:   { wrapper: 'border-l-4 border-red-500',     icon: 'text-red-500',     bar: 'bg-red-500'     },
  warning: { wrapper: 'border-l-4 border-amber-500',   icon: 'text-amber-500',   bar: 'bg-amber-500'   },
  info:    { wrapper: 'border-l-4 border-brand-500',   icon: 'text-brand-500',   bar: 'bg-brand-500'   },
};

function ToastItem({ toast }: { toast: Toast }) {
  const removeToast = useToastStore(s => s.removeToast);
  const style = STYLES[toast.type];

  return (
    <div
      className={`relative bg-white rounded-xl shadow-card-hover w-[200px] sm:min-w-[300px] sm:w-auto sm:max-w-[360px] overflow-hidden animate-fade-in ${style.wrapper}`}
    >
      {/* Content */}
      <div className="flex items-start gap-2.5 px-3 pt-2.5 pb-3 sm:gap-3 sm:px-4 sm:pt-3 sm:pb-4">
        <span className={`mt-0.5 flex-shrink-0 ${style.icon}`}>
          {ICONS[toast.type]}
        </span>
        <p className="flex-1 text-xs sm:text-sm font-semibold text-saas-text leading-snug">{toast.message}</p>
        <button
          onClick={() => removeToast(toast.id)}
          className="text-saas-muted hover:text-saas-text transition-colors mt-0.5 flex-shrink-0"
          aria-label="Dismiss"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar — shrinks over 3s using toastProgress keyframe */}
      <div
        className={`absolute bottom-0 left-0 h-1 w-full origin-left ${style.bar}`}
        style={{ animation: 'toastProgress 3s linear forwards' }}
      />
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore(s => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-3 right-3 sm:top-5 sm:right-5 z-[9999] flex flex-col gap-2">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}
